/**
 * useAudioRecorder — TASK-476.
 * Single-blob dictation recorder: one getUserMedia + MediaRecorder, stop() resolves the
 * full audio Blob. No chunking/streaming (that lives in useMeetingCapture for live meetings).
 */
import { useCallback, useRef, useState } from 'react';

/** Best supported MediaRecorder mime type (backend transcodes via ffmpeg, so any is fine). */
function pickMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return '';
  for (const mt of ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus']) {
    if (MediaRecorder.isTypeSupported(mt)) return mt;
  }
  return '';
}

export type RecorderState = 'idle' | 'recording';

export function useAudioRecorder() {
  const [state, setState] = useState<RecorderState>('idle');
  const [error, setError] = useState<string | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  const start = useCallback(async (): Promise<boolean> => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = pickMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.start();
      recorderRef.current = recorder;
      setState('recording');
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Microphone access failed');
      setState('idle');
      return false;
    }
  }, []);

  const stop = useCallback((): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const recorder = recorderRef.current;
      if (!recorder) {
        reject(new Error('Not recording'));
        return;
      }
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        recorderRef.current = null;
        setState('idle');
        resolve(blob);
      };
      recorder.stop();
    });
  }, []);

  /** Abort without producing a Blob: stop the recorder, release the mic, drop chunks. */
  const cancel = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.onstop = null; // no pending handler should resolve a stale blob
      recorder.stop();
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    recorderRef.current = null;
    chunksRef.current = [];
    setState('idle');
  }, []);

  return { state, error, start, stop, cancel };
}
